// When a task schedule runs: a preset ("Every weekday at 07:00", every day,
// a weekday each week, the 1st of the month) or a cron typed by hand, in a
// zone, read back in words with the next fire. The workflow editor's
// timetable rules (features/workflows/editor/scheduleModel.ts). A custom
// cron is passed up as typed, so the form can say what is wrong with it.

import { useState } from "react";

import { Dropdown } from "@/components/ui/Dropdown";
import { Input } from "@/components/ui/Input";
import { PRESETS, WEEKDAY_OPTIONS, cronFromDraft, cronWords, draftFromCron, nextWords, zoneLabel, zoneOptions, type ScheduleDraft, type SchedulePreset } from "@/features/workflows/editor/scheduleModel";

import { cronProblem } from "./scheduleOverviewModel";

interface CronFieldsProps {
  cron: string;
  tz: string;
  showProblem: boolean;
  onChange: (patch: { cron?: string; tz?: string }) => void;
  /** Minutes the fires must be apart (15; 1 for a reminder the operator asked to have that often). */
  floor?: number;
}

export function CronFields({ cron, tz, showProblem, onChange, floor }: CronFieldsProps) {
  const [draft, setDraft] = useState<ScheduleDraft>(() => draftFromCron(cron));
  const zones = zoneOptions(tz);
  const problem = cronProblem(cron, floor);

  function apply(d: ScheduleDraft) {
    setDraft(d);
    const next = d.preset === "custom" ? d.cron : cronFromDraft(d);
    if (next !== null && next !== cron) onChange({ cron: next });
  }
  function setPreset(preset: SchedulePreset) {
    if (preset === "custom") setDraft({ ...draft, preset, cron });
    else apply({ ...draft, preset });
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
        <Dropdown<SchedulePreset> ariaLabel="How often" size="sm" minWidth={170} matchTriggerWidth={false} value={draft.preset} options={PRESETS} onChange={setPreset} />
        {draft.preset === "weekly" && (
          <>
            <span>on</span>
            <Dropdown ariaLabel="Which day" size="sm" minWidth={120} matchTriggerWidth={false} value={String(draft.weekday)} options={WEEKDAY_OPTIONS} onChange={(v) => apply({ ...draft, weekday: Number(v) })} />
          </>
        )}
        {draft.preset !== "custom" ? (
          <>
            <span>at</span>
            <Input aria-label="Time" type="time" value={draft.time} className="w-[108px]" onChange={(e) => apply({ ...draft, time: e.target.value })} />
          </>
        ) : (
          <Input
            aria-label="Cron expression"
            value={draft.cron}
            spellCheck={false}
            placeholder="30 6 * * 1-5"
            className="w-[180px] font-mono text-sm"
            aria-invalid={problem && (showProblem || draft.cron.trim().length > 8) ? true : undefined}
            onChange={(e) => apply({ ...draft, cron: e.target.value })}
          />
        )}
        <Dropdown ariaLabel="Time zone" size="sm" minWidth={130} matchTriggerWidth={false} value={tz} options={zones.map((z) => ({ value: z, label: zoneLabel(z), hint: z === zones[0] ? "Your zone" : undefined }))} onChange={(v) => onChange({ tz: v })} />
      </div>
      <p className={problem && showProblem ? "text-xs text-danger" : "text-xs text-fg-muted"} aria-live="polite">
        {problem ? (showProblem || draft.preset === "custom" ? problem : "") : [cronWords(cron), nextWords(cron, tz)].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}
